/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductDimensionsWithAvailabilitiesFull } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import {
    BypassCache,
    ProductDimension,
    ProductDimensionAvailabilitySearchCriteria,
    ProductsDataActions,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';

import { resolveCrossEntitySelectedVariant } from './get-cross-entity-selected-variant';
import { ArrayExtensions } from './utilities';
import { isCrossCompanyInventoryLookupEcommerceEnabled } from './utilities/cross-entity-inventory-feature';
import { createInventoryAvailabilitySearchCriteria } from './utilities/product-inventory-utils';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';
import { generateProductImageUrl, getDimensionValuesFromQuery, getSelectedProductIdFromActionInput } from './utilities/utils';

/**
 * Stores data about the selected product.
 */
export interface ISelectedProduct extends SimpleProduct {
    productVariant?: SimpleProduct;
}

/**
 * Get selected variant action input class.
 */
export class SelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public matchingDimensionValues?: ProductDimension[];

    public bypassCache?: BypassCache;

    public catalogId?: number;

    public constructor(
        productId: number,
        channelId: number,
        matchingDimensionValues?: ProductDimension[],
        bypassCache?: BypassCache,
        requestContext?: IRequestContext
    ) {
        this.productId = productId;
        this.channelId = channelId;
        this.matchingDimensionValues = matchingDimensionValues;
        this.bypassCache = bypassCache;

        if (requestContext) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    /**
     * Cache key.
     * @returns String.
     */
    public getCacheKey: () => string = () => 'SelectedVariant';

    /**
     * Cache object type.
     * @returns String.
     */
    public getCacheObjectType: () => string = () => 'SimpleProduct';

    /**
     * Cache type.
     * @returns String.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns The input for the ata action getSelectedVariantNewAction.
 */
const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): SelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new SelectedVariantInput(
            +productId,
            +inputData.requestContext.apiSettings.channelId,
            undefined,
            undefined,
            inputData.requestContext
        );
    }
    throw new Error('Unable to create SelectedVariantInput, no productId found on module config or query');
};

/**
 * Checks if given product has any unmatched dimensions.
 * Modifies product entity to use matched dimension values.
 * @param product - The product to analyze and modify.
 * @param matchingDimensionValues - Matched dimensions.
 * @returns True if has any unmatched dimension, false if all dimensions are specified.
 */
const checkIfHasUnmatchedDimensions = (product: SimpleProduct, matchingDimensionValues: ProductDimension[]): boolean => {
    let hasUnmatchedDimension: boolean = false;
    if (product.Dimensions) {
        product.Dimensions = product.Dimensions.map(dimension => {
            return { ...dimension };
        });
        for (const dimension of product.Dimensions) {
            const matchedTargetDimension = matchingDimensionValues.find(
                targetDimension => targetDimension.DimensionTypeValue === dimension.DimensionTypeValue
            );

            if (matchedTargetDimension) {
                dimension.DimensionValue = matchedTargetDimension.DimensionValue;
            } else {
                hasUnmatchedDimension = true;
            }
        }
    }
    return hasUnmatchedDimension;
};

/**
 * Retrieves product dimensions with availabilities.
 * @param product - Product for which the dimensions need to be retrieved.
 * @param matchingDimensionValues - Selected dimensions - filter criteria for the dimensions API.
 * @param context - Action context.
 * @param catalogId - Catalog id.
 * @returns Array of dimensions.
 */
const getFullDimensions = async (
    product: SimpleProduct,
    matchingDimensionValues: ProductDimension[],
    context: IActionContext,
    catalogId?: number
) => {
    let fullDimensions: IProductDimensionsWithAvailabilitiesFull[] = [];

    const dimensionTypeValues: number[] = [];
    (product.Dimensions ?? []).map(dimension => {
        dimensionTypeValues.push(dimension.DimensionTypeValue);
    });
    const shippingInventoryConfiguration = createInventoryAvailabilitySearchCriteria(context, [], true, undefined, undefined);
    const searchCriteria: ProductDimensionAvailabilitySearchCriteria = {
        RequestedDimensionTypeValues: dimensionTypeValues,
        MatchingDimensionValues: matchingDimensionValues,
        DefaultWarehouseOnly: shippingInventoryConfiguration.DefaultWarehouseOnly,
        FilterByChannelFulfillmentGroup: shippingInventoryConfiguration.FilterByChannelFulfillmentGroup,
        DeliveryModeTypeFilterValue: shippingInventoryConfiguration.DeliveryModeTypeFilterValue,
        CatalogId: catalogId
    };

    const dimensionValuesPromise = ProductsDataActions.getDimensionValuesWithEstimatedAvailabilitiesAsync(
        { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
        product.MasterProductId ? product.MasterProductId : product.RecordId,
        searchCriteria
    );
    const fullDimensionPromises = (product.Dimensions ?? []).map(productDimension => {
        return dimensionValuesPromise.then(dimensionValuesWithAvailabilities => {
            const dimensionValueWithAvailability = dimensionValuesWithAvailabilities.filter(
                dimension => dimension.DimensionTypeValue === productDimension.DimensionTypeValue
            );
            const fullDimension: IProductDimensionsWithAvailabilitiesFull = {
                ...productDimension,
                dimensionValuesWithInventory: dimensionValueWithAvailability
            };
            return fullDimension;
        });
    });
    fullDimensions = await Promise.all(fullDimensionPromises);
    return fullDimensions;
};

/**
 * Retrieves product variant for the given input or undefined if not found.
 * @param input - Action input.
 * @param context - Action context.
 * @param fullDimensions - Product dimensions with availabilities.
 * @returns A product variant that is available or undefined.
 */
const getProductVariant = async (
    input: SelectedVariantInput,
    context: IActionContext,
    fullDimensions: IProductDimensionsWithAvailabilitiesFull[]
) => {
    let productVariant: SimpleProduct | undefined;
    if (
        ArrayExtensions.hasElements(fullDimensions) &&
        ArrayExtensions.hasElements(fullDimensions[0].dimensionValuesWithInventory) &&
        ArrayExtensions.hasElements(fullDimensions[0].dimensionValuesWithInventory[0].ProductIds)
    ) {
        const variantProductId = fullDimensions[0].dimensionValuesWithInventory[0].ProductIds[0];
        const result = await ProductsDataActions.getByIdsAsync(
            {
                callerContext: context,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context),
                bypassCache: input.bypassCache
            },
            input.channelId,
            [variantProductId],
            null,
            input.catalogId ?? 0
        );
        productVariant = result[0];
        productVariant = { ...productVariant };
        const variantImageUrl = generateProductImageUrl(productVariant, context.requestContext.apiSettings);

        if (variantImageUrl) {
            productVariant.PrimaryImageUrl = variantImageUrl;
        }
    }

    return productVariant;
};

/**
 * Action method for the getSelectedVariant data action.
 * @param input - The action input class.
 * @param context - The action context.
 * @returns Simple product.
 */
async function getSelectedVariantAction(input: SelectedVariantInput, context: IActionContext): Promise<ISelectedProduct | null> {
    const matchingDimensionValues = input.matchingDimensionValues ?? getDimensionValuesFromQuery(context.requestContext.url.requestUrl);

    const result = await ProductsDataActions.getByIdsAsync(
        {
            callerContext: context,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context),
            bypassCache: input.bypassCache
        },
        input.channelId,
        [input.productId],
        null,
        input.catalogId ?? 0
    );
    let product = result[0];

    // Need to dereference this before editing it. Otherwise we might not
    // properly get the mobx events because if things aren't properly observable
    // they won't fire when you set them, and then if you don't deref the value in
    // the cache will match the value when you try to save it, so it won't detect any
    // changes there either
    product = { ...product };
    const hasUnmatchedDimension = checkIfHasUnmatchedDimensions(product, matchingDimensionValues);

    const newImageUrl = generateProductImageUrl(product, context.requestContext.apiSettings);

    if (newImageUrl) {
        product.PrimaryImageUrl = newImageUrl;
    }

    // Retrieve product variants only if matching dimensions are provided.
    let productVariant: SimpleProduct | undefined;
    if (ArrayExtensions.hasElements(matchingDimensionValues)) {
        if (isCrossCompanyInventoryLookupEcommerceEnabled(context.requestContext.app.config)) {
            const resolution = await resolveCrossEntitySelectedVariant(
                context,
                product.MasterProductId ? product.MasterProductId : product.RecordId,
                input.channelId,
                matchingDimensionValues,
                { bypassCache: input.bypassCache }
            );

            productVariant = resolution.source === 'CrossLE' ? resolution.variant : undefined;
        }

        if (!productVariant) {
            const fullDimensions: IProductDimensionsWithAvailabilitiesFull[] = await getFullDimensions(
                product,
                matchingDimensionValues,
                context,
                input.catalogId
            );

            productVariant = await getProductVariant(input, context, fullDimensions);
        }
    }

    // Update dimension value back to the one that comes from API since the URL query might have a different format for the dimension values.
    if (productVariant?.Dimensions && product.Dimensions) {
        for (const dimension of product.Dimensions) {
            const matchedTargetDimensionFromApi = productVariant.Dimensions.find(
                targetDimension => targetDimension.DimensionTypeValue === dimension.DimensionTypeValue
            );
            const matchedTargetDimensionFromSearch = matchingDimensionValues.find(
                targetDimension => targetDimension.DimensionTypeValue === dimension.DimensionTypeValue
            );

            if (matchedTargetDimensionFromSearch && matchedTargetDimensionFromApi) {
                dimension.DimensionValue = matchedTargetDimensionFromApi.DimensionValue;
            }
        }
    }

    if (!hasUnmatchedDimension && ArrayExtensions.hasElements(matchingDimensionValues) && productVariant) {
        return productVariant;
    }

    const selectedProduct: ISelectedProduct = product;
    selectedProduct.productVariant = productVariant;
    return selectedProduct;
}

/**
 * The complete getSelectedVariant data action.
 */
export const getSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-selected-variant',
    action: getSelectedVariantAction as IAction<ISelectedProduct | null>,
    input: createInput
});

export default getSelectedVariantActionDataAction;
